import { useState } from 'react';
import { MessageCircleQuestion } from 'lucide-react';

import { cn } from '@/helpers/shadcn';

import { Button } from '@/app/ui/button';
import { Checkbox } from '@/app/ui/checkbox';
import { Input } from '@/app/ui/input';

/**
 * A question from the assistant with choices to click. The answer goes back as
 * the user's next message, which is also what ends it: once anything has been
 * said after it, it is no longer `isPending` and the choices stop responding.
 *
 * Options are model output, so two can read the same: each is known by its
 * place, not its text.
 */
const AskBlock = ({ block, isPending, onAnswer }) => {
  const [selected, setSelected] = useState([]);
  const [text, setText] = useState('');
  const options = block.options ?? [];

  const handleChoose = (index) => {
    if (!block.multi_select) {
      onAnswer(options[index]);
      return;
    }
    setSelected((current) => (current.includes(index) ? current.filter((item) => item !== index) : [...current, index]));
  };

  // In the order the options are listed, not the order they were clicked.
  const handleSendSelected = () => onAnswer(options.filter((_option, index) => selected.includes(index)).join(', '));

  const handleTextChange = (event) => setText(event.target.value);

  const handleSendText = (event) => {
    event.preventDefault();
    if (text.trim()) onAnswer(text.trim());
  };

  return (
    <div className="bg-card my-3 rounded-lg border p-3" data-testid="agent-ask">
      <div className="mb-2.5 flex items-start gap-2 text-sm font-medium">
        <MessageCircleQuestion className="text-primary mt-0.5 size-4 shrink-0" />
        {block.question}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option, index) => (
          // Options have no identity but their place; two may share a label.
          // eslint-disable-next-line react/no-array-index-key
          <AskOption key={index} index={index} option={option} isMulti={block.multi_select} isSelected={selected.includes(index)} isPending={isPending} onChoose={handleChoose} />
        ))}
      </div>
      {isPending && block.multi_select && (
        <Button size="sm" className="mt-2.5" disabled={!selected.length} onClick={handleSendSelected} data-testid="agent-ask-send">
          Send Answer
        </Button>
      )}
      {isPending && block.allow_text && (
        <form onSubmit={handleSendText} className="mt-2.5 flex gap-1.5">
          <Input value={text} onChange={handleTextChange} placeholder="Or type an answer…" aria-label="Your answer" className="h-7 text-sm" data-testid="agent-ask-text-input" />
          <Button type="submit" size="sm" variant="outline" disabled={!text.trim()} data-testid="agent-ask-text-send">
            Send
          </Button>
        </form>
      )}
    </div>
  );
};

const AskOption = ({ index, option, isMulti, isSelected, isPending, onChoose }) => {
  const handleClick = () => onChoose(index);

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!isPending}
      onClick={handleClick}
      aria-pressed={isMulti ? isSelected : undefined}
      // max-w-full: Button cannot shrink, so a long option would run off the
      // panel at its one-line width; capped at the row, its label wraps.
      className={cn('h-auto min-h-7 max-w-full justify-start py-1 text-left whitespace-normal', isSelected && 'border-primary bg-primary/10')}
      data-testid={`agent-ask-option-${index}`}
    >
      {isMulti && <Checkbox checked={isSelected} tabIndex={-1} aria-hidden className="pointer-events-none" />}
      {option}
    </Button>
  );
};

export default AskBlock;
